// src/context/jobReducer.js

// --- Action Types for the Job Reducer ---
export const actionTypes = {
  SET_DISCOVERY_JOB_STATE: 'SET_DISCOVERY_JOB_STATE',
  CLEAR_DISCOVERY_JOB_STATE: 'CLEAR_DISCOVERY_JOB_STATE',
  CLEAR_DISCOVERY_JOB_ERROR: 'CLEAR_DISCOVERY_JOB_ERROR',
  SET_CONTENT_ANALYSIS_JOB_STATE: 'SET_CONTENT_ANALYSIS_JOB_STATE',
  CLEAR_CONTENT_ANALYSIS_JOB_STATE: 'CLEAR_CONTENT_ANALYSIS_JOB_STATE',
  CLEAR_CONTENT_ANALYSIS_JOB_ERROR: 'CLEAR_CONTENT_ANALYSIS_JOB_ERROR',
  // --- NEW ACTION TYPES FOR UI UPDATES ---
  // We use a different naming convention to be clear these are for UI manipulation
  UPDATE_JOB_RESULTS_AFTER_IMPORT: 'UPDATE_JOB_RESULTS_AFTER_IMPORT',
  UPDATE_JOB_RESULTS_AFTER_MOVE_TO_HOLD:
    'UPDATE_JOB_RESULTS_AFTER_MOVE_TO_HOLD',
  UPDATE_JOB_RESULTS_AFTER_DISCARD: 'UPDATE_JOB_RESULTS_AFTER_DISCARD',
  // --- NEW: AI Agent Job Actions ---
  SET_AGENT_JOB_HISTORY: 'SET_AGENT_JOB_HISTORY',
  UPDATE_AGENT_JOB_STATUS: 'UPDATE_AGENT_JOB_STATUS', // For polling updates
  REMOVE_AGENT_JOB: 'REMOVE_AGENT_JOB',
  SET_SELECTED_JOB_DETAILS: 'SET_SELECTED_JOB_DETAILS',
  // --- NEW: Agent Chat Actions ---
  SET_AGENT_CHATS_LIST: 'SET_AGENT_CHATS_LIST',
  SET_ACTIVE_AGENT_CHAT_ID: 'SET_ACTIVE_AGENT_CHAT_ID',
  ADD_AGENT_CHAT_TO_LIST: 'ADD_AGENT_CHAT_TO_LIST',
};

// --- Initial State Shape for Jobs ---
export const initialJobState = {
  discoveryJob: {
    jobId: null,
    jobStatus: 'idle', // 'idle', 'starting', 'pending', 'finished', 'failed'
    jobStartTime: null,
    results: null,
    error: null,
  },
  contentAnalysisJob: {
    jobId: null,
    influencerId: null,
    jobStatus: 'idle',
    message: null,
    results: null,
    error: null,
  },
  // --- NEW: AI Agent Job State ---
  agentJobHistory: [], // Holds the list of jobs for the control panel
  selectedJobDetails: null, // Holds details of a clicked job for the modal
  selectedJobType: null,
  isJobDetailsModalOpen: false, // Controls visibility of the details modal
  // --- NEW: Agent Chat State ---
  agentChatsList: [], // Array of { chatId, title, updatedAt }
  activeAgentChatId: null, // The ID currently loaded in the window
};

// --- Reducer Function: A pure function that calculates the next state ---
export const jobReducer = (state, action) => {
  console.log('[jobReducer] Action received:', action.type);
  switch (action.type) {
    case actionTypes.SET_DISCOVERY_JOB_STATE:
      return {
        ...state,
        discoveryJob: { ...state.discoveryJob, ...action.payload },
      };
    case actionTypes.CLEAR_DISCOVERY_JOB_STATE:
      return { ...state, discoveryJob: initialJobState.discoveryJob };
    case actionTypes.CLEAR_DISCOVERY_JOB_ERROR:
      return {
        ...state,
        discoveryJob: {
          ...state.discoveryJob,
          error: null,
          results: state.discoveryJob.results
            ? { ...state.discoveryJob.results, warnings: [] }
            : null,
        },
      };
    case actionTypes.SET_CONTENT_ANALYSIS_JOB_STATE:
      return {
        ...state,
        contentAnalysisJob: { ...state.contentAnalysisJob, ...action.payload },
      };
    case actionTypes.CLEAR_CONTENT_ANALYSIS_JOB_STATE:
      return {
        ...state,
        contentAnalysisJob: initialJobState.contentAnalysisJob,
      };
    case actionTypes.CLEAR_CONTENT_ANALYSIS_JOB_ERROR:
      return {
        ...state,
        contentAnalysisJob: {
          ...state.contentAnalysisJob,
          error: null,
          results: state.contentAnalysisJob.results
            ? { ...state.contentAnalysisJob.results, warnings: [] }
            : null,
        },
      };

    case actionTypes.UPDATE_JOB_RESULTS_AFTER_IMPORT: {
      // Payload is an array of platformIds to remove
      if (!state.discoveryJob?.results) return state;
      const idsToRemove = new Set(action.payload);
      return {
        ...state,
        discoveryJob: {
          ...state.discoveryJob,
          results: {
            ...state.discoveryJob.results,
            newInfluencers: state.discoveryJob.results.newInfluencers.filter(
              (inf) => !idsToRemove.has(inf.platformId),
            ),
            similarInfluencers: (
              state.discoveryJob.results.similarInfluencers || []
            ).filter((inf) => !idsToRemove.has(inf.platformId)),
            // --- NEW: Remove them from the AI Rejects list when rescued! ---
            aiRejectedInfluencers: (
              state.discoveryJob.results.aiRejectedInfluencers || []
            ).filter((inf) => !idsToRemove.has(inf.platformId)),
          },
        },
      };
    }


    case actionTypes.UPDATE_JOB_RESULTS_AFTER_MOVE_TO_HOLD: {
      // Payload is the array of new on_hold records from the API
      if (!state.discoveryJob?.results || !action.payload) return state;
      const onHoldRecordsToAdd = action.payload;
      const idsToMove = new Set(
        onHoldRecordsToAdd.map((inf) => inf.platformId),
      );
      return {
        ...state,
        discoveryJob: {
          ...state.discoveryJob,
          results: {
            ...state.discoveryJob.results,
            newInfluencers: state.discoveryJob.results.newInfluencers.filter(
              (inf) => !idsToMove.has(inf.platformId),
            ),
            onHoldInfluencers: [
              ...(state.discoveryJob.results.onHoldInfluencers || []),
              ...onHoldRecordsToAdd,
            ],
          },
        },
      };
    }

    case actionTypes.UPDATE_JOB_RESULTS_AFTER_DISCARD: {
      // Payload is the array of discarded records from the API
      if (!state.discoveryJob?.results || !action.payload) return state;
      const discardedRecords = action.payload;
      const idsToMove = new Set(discardedRecords.map((inf) => inf.platformId));
      return {
        ...state,
        discoveryJob: {
          ...state.discoveryJob,
          results: {
            ...state.discoveryJob.results,
            newInfluencers: state.discoveryJob.results.newInfluencers.filter(
              (inf) => !idsToMove.has(inf.platformId),
            ),
            similarInfluencers: (
              state.discoveryJob.results.similarInfluencers || []
            ).filter((inf) => !idsToMove.has(inf.platformId)),
            onHoldInfluencers: (
              state.discoveryJob.results.onHoldInfluencers || []
            ).filter((inf) => !idsToMove.has(inf.platformId)),
            discardedInfluencers: [
              ...(state.discoveryJob.results.discardedInfluencers || []),
              ...discardedRecords,
            ],
          },
        },
      };
    }
    // --- NEW: AI Agent Reducer Cases ---
    case actionTypes.SET_AGENT_JOB_HISTORY:
      return { ...state, agentJobHistory: action.payload };

    case actionTypes.UPDATE_AGENT_JOB_STATUS: {
      // Updates a specific job in the list (e.g., changing 'running' to 'canceled')
      const updatedJob = action.payload;
      return {
        ...state,
        agentJobHistory: state.agentJobHistory.map((job) =>
          job.id === updatedJob.id ? { ...job, ...updatedJob } : job,
        ),
      };
    }

    case actionTypes.REMOVE_AGENT_JOB: {
      // Removes a job from the list entirely
      const jobIdToRemove = action.payload;
      return {
        ...state,
        agentJobHistory: state.agentJobHistory.filter(
          (job) => job.id !== jobIdToRemove,
        ),
      };
    }

    case actionTypes.SET_SELECTED_JOB_DETAILS:
      // Logic: If payload is null, we are closing.
      // If payload exists, we expect an object: { details: [], jobType: string }

      if (!action.payload) {
        // Closing the modal
        return {
          ...state,
          selectedJobDetails: null,
          selectedJobType: null,
          isJobDetailsModalOpen: false,
        };
      }

      return {
        ...state,
        selectedJobDetails: action.payload.details, // Extract the array
        selectedJobType: action.payload.jobType, // Extract the type
        isJobDetailsModalOpen: true,
      };

    // --- NEW: Agent Chat Reducers ---
    case actionTypes.SET_AGENT_CHATS_LIST:
      return { ...state, agentChatsList: action.payload };

    case actionTypes.SET_ACTIVE_AGENT_CHAT_ID:
      return { ...state, activeAgentChatId: action.payload };

    case actionTypes.ADD_AGENT_CHAT_TO_LIST:
      // Add new chat to the top of the list
      const newChat = action.payload;
      // Prevent duplicates just in case
      const filteredList = state.agentChatsList.filter(
        (c) => c.chatId !== newChat.chatId,
      );
      return {
        ...state,
        agentChatsList: [newChat, ...filteredList],
      };

    default:
      return state;
  }
};
